/**
 * Preset schema migrations.
 *
 * Each entry takes a preset at version N and returns it at version N+1.
 * Entries are applied in order until a preset reaches CURRENT_SCHEMA_VERSION.
 *
 * Guidelines:
 *   - Keep steps pure — no I/O, no reading from runtime state.
 *   - Drop unknown or invalid data with a warning rather than throwing, so
 *     a preset never fails to load for trivial shape drift.
 *   - Test each step in isolation (one test per migration).
 *
 * Versioning policy: bump CURRENT_SCHEMA_VERSION in schema.ts, add a step
 * here, add a test. A new field with a sensible default usually only needs
 * a step that supplies the default — existing user presets pick it up
 * automatically on next load.
 */

import {
  CURRENT_SCHEMA_VERSION,
  DEFAULT_PRESET_CATEGORY,
  type Preset,
} from './schema';

type Migration = (input: Record<string, unknown>) => Record<string, unknown>;

/** Module types that the v3→v4 migration considers "effects" — the ones
 *  that lived in the dynamic chain pre-refactor. Anything else stays in
 *  the `modules` list (OSC, FILT, ENV, VCA, LFO, S&H, NOISE, ARP, …). */
const EFFECT_TYPES_V3 = new Set([
  'compressor',
  'distortion',
  'ringmod',
  'chorus',
  'flanger',
  'phaser',
  'delay',
  'bitcrusher',
  'reverb',
]);

/**
 * v1 → v2: add `category` field. Older presets lacked any grouping, so we
 * drop them into the DEFAULT_PRESET_CATEGORY bucket — the user can
 * recategorize them by clicking Update.
 */
const v1ToV2: Migration = (input) => {
  return {
    ...input,
    category:
      typeof input.category === 'string' && input.category
        ? input.category
        : DEFAULT_PRESET_CATEGORY,
  };
};

/**
 * v2 → v3: add optional `effectsOrder`. Pre-v3 presets don't record the
 * effect chain order, so on load they implicitly get the default order
 * at the time of save. Passing the object through unchanged is the
 * correct v3 shape — the field is optional and the runtime falls back
 * to the current default order when it's missing.
 */
const v2ToV3: Migration = (input) => input;

/**
 * v3 → v4: split effects out of `modules` into a dedicated `effects`
 * list. Pre-v4 presets stored every effect's params alongside non-
 * effect modules (OSC, FILT, …) and used a parallel `effectsOrder`
 * array of types to record chain order. v4 makes effects first-class:
 * each entry has its own type/id/params, allowing duplicates.
 *
 * Strategy:
 *   1. Pull every entry from `modules` whose type is in EFFECT_TYPES_V3
 *      and stash it as a v4 `PresetEffectInstance` keyed by type.
 *   2. Read `effectsOrder` (or fall back to the order modules appeared)
 *      and emit one `effects` entry per type in that order.
 *   3. Strip the now-relocated entries from `modules` so the v4 module
 *      list only holds non-effect modules.
 *
 * The stored module ids are reused as effect instance ids — that
 * preserves any modConnections targeting them. Pre-v3 presets passed
 * through v2ToV3 still hit this step, so they migrate cleanly even
 * though they don't have effectsOrder.
 */
const v3ToV4: Migration = (input) => {
  const modulesIn = Array.isArray(input.modules) ? (input.modules as ModulesInput) : [];
  const order = Array.isArray(input.effectsOrder)
    ? (input.effectsOrder as string[]).filter((t) => typeof t === 'string')
    : [];

  // Bucket by type so the order array can pick out the right state.
  const effectsByType = new Map<string, ModuleStateLike>();
  const remainingModules: ModuleStateLike[] = [];
  for (const m of modulesIn) {
    if (m && typeof m === 'object' && typeof m.type === 'string') {
      if (EFFECT_TYPES_V3.has(m.type)) {
        // Last write wins — but pre-v4 presets only had one of each type,
        // so this is effectively a single insert per bucket.
        effectsByType.set(m.type, m);
        continue;
      }
    }
    remainingModules.push(m);
  }

  // Final order: explicit effectsOrder first; then any effect that wasn't
  // mentioned (newer effects added after the preset was saved) appended
  // in their original document order.
  const seen = new Set<string>();
  const orderedTypes = [
    ...order.filter((t) => effectsByType.has(t) && (seen.add(t) || true)),
    ...[...effectsByType.keys()].filter((t) => !seen.has(t)),
  ];

  const effects = orderedTypes
    .map((type) => effectsByType.get(type))
    .filter((m): m is ModuleStateLike => !!m)
    .map((m) => ({
      type: m.type as string,
      // Reuse the saved id so modConnections referencing this effect
      // still resolve. Pre-v3 presets had deterministic ids ('compressor',
      // 'dist', 'delay', etc.); v4 keeps using whatever was on disk.
      id: typeof m.id === 'string' && m.id ? m.id : (m.type as string),
      bypass: m.bypass === true,
      params:
        m.params && typeof m.params === 'object'
          ? (m.params as Record<string, number | string>)
          : {},
    }));

  return {
    ...input,
    modules: remainingModules,
    effects,
  };
};

/** Loose ModuleState shape — what migrations see before typing it. */
interface ModuleStateLike {
  type?: unknown;
  id?: unknown;
  bypass?: unknown;
  params?: unknown;
}
type ModulesInput = ModuleStateLike[];

/**
 * v4 → v5: add empty `midiBindings` field. New shape, no data
 * migration needed — presets that pre-date MIDI Learn just have no
 * bindings, so the runtime treats it as nothing to apply.
 */
const v4ToV5: Migration = (input) => ({
  ...input,
  midiBindings: Array.isArray(input.midiBindings) ? input.midiBindings : [],
});

/**
 * Ordered list of migrations. Index 0 takes v1 → v2, index 1 takes v2 → v3,
 * etc.
 */
const MIGRATIONS: readonly Migration[] = [v1ToV2, v2ToV3, v3ToV4, v4ToV5];

/**
 * Run an opaque stored preset through every applicable migration step and
 * return it as a typed Preset (or null if it's unsalvageable).
 */
export function migratePreset(stored: unknown): Preset | null {
  if (!stored || typeof stored !== 'object') return null;
  let current = stored as Record<string, unknown>;

  const version = typeof current.schemaVersion === 'number' ? current.schemaVersion : 0;
  if (version > CURRENT_SCHEMA_VERSION) {
    // A newer-than-supported preset landed (future version migration wasn't
    // shipped yet). Refuse rather than discarding fields silently.
    console.warn(
      `[preset] ignoring preset with schemaVersion=${version} > current ${CURRENT_SCHEMA_VERSION}`,
    );
    return null;
  }

  for (let v = Math.max(1, version); v < CURRENT_SCHEMA_VERSION; v++) {
    const step = MIGRATIONS[v - 1];
    if (!step) return null;
    current = step(current);
  }
  current.schemaVersion = CURRENT_SCHEMA_VERSION;
  return current as unknown as Preset;
}
